import { useState, useEffect, useRef } from "react";
import { Plus, Edit3, Trash2, Search, X, Check } from "lucide-react";
import { useData } from "../../context/DataContext";
// useRole available from ../../hooks/useRole if needed
import { Modal } from "./Modal";
import { FormField } from "./FormField";
import { DataTable } from "./DataTable";

const inputClass = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/30 transition-colors";
const btnPrimary = "bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors";
const btnSecondary = "bg-white/10 hover:bg-white/15 text-white/70 px-4 py-2 rounded-lg text-sm font-medium transition-colors";

const generateId = () => crypto.randomUUID?.() || Math.random().toString(36).slice(2, 10);

export function CrudPage({ title, subtitle, dataKey, columns, formFields, defaultForm, getId, getDisplay, onBeforeSave, onAfterSave, onAfterEdit }) {
  const { data, setData } = useData();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [newRows, setNewRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const searchTimer = useRef(null);

  // Debounce search input by 250ms
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 250);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search]);

  const records = data[dataKey] || [];
  // Use targeted field matching instead of JSON.stringify for better performance
  const searchFields = formFields.map(f => f.key);
  const filtered = debouncedSearch
    ? (() => {
        const term = debouncedSearch.toLowerCase();
        return records.filter(r => searchFields.some(k => r[k] != null && String(r[k]).toLowerCase().includes(term)));
      })()
    : records;
  const idKeyMap = { items: "item_id", vendors: "vendor_id", employees: "employee_id", documents: "doc_id", clients: "client_id", equipment: "equipment_id", warehouses: "warehouse_id" };
  const idKey = idKeyMap[dataKey] || "id";

  // Open bulk add popup with 5 empty rows
  const openBulkAdd = () => {
    const rows = Array.from({ length: 5 }, () => ({ _tempId: generateId(), ...(typeof defaultForm === "function" ? defaultForm() : { ...defaultForm }) }));
    setNewRows(rows);
    setShowBulkAdd(true);
  };

  const addMoreRows = (count = 3) => {
    setNewRows(prev => [...prev, ...Array.from({ length: count }, () => ({ _tempId: generateId(), ...(typeof defaultForm === "function" ? defaultForm() : { ...defaultForm }) }))]);
  };

  // Save valid rows from bulk popup
  const saveBulkRows = async () => {
    const required = formFields.filter(f => f.required).map(f => f.key);
    const valid = newRows.filter(r => required.every(k => r[k] && String(r[k]).trim()));
    if (!valid.length) return;
    const processed = [];
    for (const r of valid) {
      const rec = { ...r };
      delete rec._tempId;
      rec[idKey] = generateId();
      processed.push(onBeforeSave ? await onBeforeSave(rec) : rec);
    }
    setData(d => ({
      ...d,
      [dataKey]: [...d[dataKey], ...processed]
    }));
    setShowBulkAdd(false);
    setNewRows([]);
    // Notify parent of newly created records
    if (onAfterSave) {
      for (const rec of processed) {
        try { await onAfterSave(rec); } catch (e) { console.warn("onAfterSave error:", e); }
      }
    }
  };

  // Edit via modal
  const openEdit = (r) => { setEditForm({ ...r }); setEditing(getId(r)); };
  const saveEdit = async () => {
    const finalForm = onBeforeSave ? await onBeforeSave(editForm) : editForm;
    setData(d => ({ ...d, [dataKey]: d[dataKey].map(r => getId(r) === editing ? { ...r, ...finalForm } : r) }));
    setEditing(null);
    // Notify parent of edited record
    if (onAfterEdit) {
      try { await onAfterEdit(finalForm); } catch (e) { console.warn("onAfterEdit error:", e); }
    }
  };

  const deleteRecord = (recordId) => {
    if (!window.confirm("Delete this record?")) return;
    setData(d => ({ ...d, [dataKey]: d[dataKey].filter(r => getId(r) !== recordId) }));
  };

  // Render editable cell inside bulk popup
  const renderCell = (ff, value, onChange) => {
    const cs = "w-full bg-transparent border-0 text-sm text-white/80 outline-none placeholder-white/20 py-1";
    if (ff.type === "select") return <select className={cs} value={value ?? ""} onChange={e => onChange(e.target.value)} style={{ background: "transparent" }}><option value="">—</option>{ff.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
    if (ff.type === "checkbox") return <input type="checkbox" checked={value ?? false} onChange={e => onChange(e.target.checked)} />;
    return <input type={ff.type || "text"} className={cs} value={value ?? ""} onChange={e => onChange(ff.type === "number" ? Number(e.target.value) : e.target.value)} placeholder={ff.placeholder || ff.label} />;
  };

  const validCount = (() => {
    const req = formFields.filter(f => f.required).map(f => f.key);
    return newRows.filter(r => req.every(k => r[k] && String(r[k]).trim())).length;
  })();

  // Read-only display columns with edit/delete actions
  const allCols = [
    ...columns,
    { header: "", render: r => (
      <div className="flex gap-0.5">
        <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="p-1 rounded hover:bg-white/10 text-white/20 hover:text-sky-400"><Edit3 size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); deleteRecord(getId(r)); }} className="p-1 rounded hover:bg-red-500/20 text-white/10 hover:text-red-400"><Trash2 size={13} /></button>
      </div>
    )},
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">{title}</h1>
          <p className="text-sm text-white/40">{subtitle || `${records.length} records`}</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input className={inputClass + " pl-8 w-48"} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button onClick={openBulkAdd} className={btnPrimary}><Plus size={14} className="inline mr-1" /> Add {title}</button>
        </div>
      </div>

      {/* Main read-only table */}
      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
        <DataTable columns={allCols} data={filtered} emptyMsg={`No ${title.toLowerCase()} yet — click "Add ${title}" to get started`} />
      </div>

      {/* Bulk Add Popup */}
      {showBulkAdd && (
        <Modal title={`Add ${title}`} onClose={() => { setShowBulkAdd(false); setNewRows([]); }} wide>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#1a1d24]">
                <tr className="border-b border-white/10">
                  <th className="py-2 px-2 text-left text-xs text-white/30 w-8">#</th>
                  {formFields.filter(f => !f.hidden).map((ff, i) => (
                    <th key={i} className="py-2 px-2 text-left text-xs uppercase tracking-wider text-white/30 font-medium">{ff.label}{ff.required ? " *" : ""}</th>
                  ))}
                  <th className="py-2 px-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {newRows.map((r, idx) => (
                  <tr key={r._tempId} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-1 px-2 text-xs text-white/20">{idx + 1}</td>
                    {formFields.filter(f => !f.hidden).map((ff, i) => (
                      <td key={i} className="py-1 px-2">
                        {renderCell(ff, r[ff.key], (val) => {
                          setNewRows(prev => prev.map((row, j) => j === idx ? { ...row, [ff.key]: val } : row));
                        })}
                      </td>
                    ))}
                    <td className="py-1 px-2">
                      <button onClick={() => setNewRows(prev => prev.filter((_, j) => j !== idx))} className="p-0.5 rounded hover:bg-red-500/20 text-white/15 hover:text-red-400"><X size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-3">
            <div className="flex gap-2 text-xs">
              <button onClick={() => addMoreRows(3)} className="text-sky-400 hover:text-sky-300">+ 3 more rows</button>
              <button onClick={() => addMoreRows(10)} className="text-sky-400 hover:text-sky-300">+ 10 rows</button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/30">{validCount} of {newRows.length} rows ready</span>
              <button onClick={() => { setShowBulkAdd(false); setNewRows([]); }} className={btnSecondary}>Cancel</button>
              <button onClick={saveBulkRows} disabled={!validCount} className={validCount ? btnPrimary : "bg-white/10 text-white/30 px-4 py-2 rounded-lg text-sm cursor-not-allowed"}><Check size={14} className="inline mr-1" /> Save {validCount} Record{validCount !== 1 ? "s" : ""}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit single record modal */}
      {editing && (
        <Modal title={`Edit ${title.replace(/s$/, "")}`} onClose={() => setEditing(null)} wide={formFields.length > 4}>
          <div className={`grid ${formFields.length > 3 ? "grid-cols-2" : "grid-cols-1"} gap-4`}>
            {formFields.map((ff, i) => (
              <div key={i}>
                <FormField label={ff.label} required={ff.required}>
                  {ff.type === "select" ? (
                    <select className={inputClass} value={editForm[ff.key] ?? ""} onChange={e => setEditForm(f => ({ ...f, [ff.key]: e.target.value }))}>
                      <option value="">Select...</option>
                      {ff.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : ff.type === "checkbox" ? (
                    <input type="checkbox" checked={editForm[ff.key] ?? false} onChange={e => setEditForm(f => ({ ...f, [ff.key]: e.target.checked }))} />
                  ) : (
                    <input type={ff.type || "text"} className={inputClass} value={editForm[ff.key] ?? ""} onChange={e => setEditForm(f => ({ ...f, [ff.key]: ff.type === "number" ? Number(e.target.value) : e.target.value }))} />
                  )}
                </FormField>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className={btnSecondary}>Cancel</button>
            <button onClick={saveEdit} className={btnPrimary}>Save Changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
